import { DocumentReference, Timestamp } from '@google-cloud/firestore';
import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityJobRunOptions,
  IDestinationEntityRecord,
  IGetRecordResponse,
  IHasSourceIdentifier,
  IMigratedDataSummary,
  IPracticeMigration,
  ISourceEntityHandler,
  ISourceEntityRecord,
  ITranslationMap,
  SkippedDestinationEntityRecord,
  type MergeConflictDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  SterilisationStatus,
  type IBrand,
  type IPractice,
  type IStaffer,
  type ISterilisationCycle,
  type SterilisableItem,
  type ISterilisationMachine,
  type ISterilisationRecord,
} from '@principle-theorem/principle-core/interfaces';
import {
  asDocRef,
  Firestore,
  FirestoreMigrate,
  getError,
  PrimaryKey,
  snapshotCombineLatest,
  toNamedDocument,
  toTimestamp,
  WithPrimaryKey,
  type WithRef,
} from '@principle-theorem/shared';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BaseDestinationEntity } from '../../../destination/base-destination-entity';
import { DestinationEntity } from '../../../destination/destination-entity';
import { TranslationMapHandler } from '../../../translation-map';
import { SterilisationRecordSourceEntity } from '../../source/entities/sterilisation-records';
import {
  D4WUnusedSterilisationRecord,
  UNUSED_STERILISATION_RECORD_RESOURCE_TYPE,
  UnusedSterilisationRecordSourceEntity,
} from '../../source/entities/unused-sterilisation-records';
import { D4WPracticeMappingHandler } from '../mappings/practices';
import { D4WStafferMappingHandler, resolveD4WStaffer } from '../mappings/staff';
import { D4WSterilisationEquipmentMappingHandler } from '../mappings/sterilisation-equipment';
import { D4WSterilisationMachineMappingHandler } from '../mappings/sterilisation-machine';
import { StafferDestinationEntity } from './staff';
import { D4WSterilisationCycleDestinationEntity } from './sterilisation-cycle';
import { D4WSterilisationMachineDestinationEntity } from './sterilisation-machine';
import { D4WSterilisationEquipmentDestinationEntity } from './sterilisations-equipment';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { PracticeMigration } from '../../../practice-migrations';
import {
  hasMergeConflicts,
  Practice,
  stafferToNamedDoc,
  SterilisationRecord,
} from '@principle-theorem/principle-core';
import { STERILISATION_CYCLE_RESOURCE_TYPE } from '../../../destination/entities/sterilisation-cycle';
import { resolveMappedItem } from '../../../base-custom-mapping-handler';
import { STERILISATION_EQUIPMENT_RESOURCE_TYPE } from '../../../mappings/sterilisation-equipment';

export const UNUSED_STERILISATION_RECORD_DESTINATION_ENTITY =
  DestinationEntity.init({
    metadata: {
      key: UNUSED_STERILISATION_RECORD_RESOURCE_TYPE,
      label: 'Unused Sterilisation Records',
      description: `Records linked to Cycles but not to patients or treatments`,
    },
  });

interface IUnusedSterilisationRecordJobData {
  brand: WithRef<IBrand>;
  practices: WithPrimaryKey<ITranslationMap<IPractice>>[];
  sterilisationMachines: WithPrimaryKey<
    ITranslationMap<ISterilisationMachine>
  >[];
  sterilisationEquipment: WithPrimaryKey<ITranslationMap<SterilisableItem>>[];
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  sourceSterilisationRecord: IGetRecordResponse<D4WUnusedSterilisationRecord>;
}

interface IUnusedSterilisationRecordMigrationData {
  sterilisationRecord: ISterilisationRecord &
    IHasSourceIdentifier & {
      createdAt: Timestamp;
    };
}

interface IUnusedSterilisationRecordSuccessData {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
  sterilisationRef: DocumentReference<ISterilisationRecord>;
}

export class UnusedSterilisationRecordDestinationEntity extends BaseDestinationEntity<
  IUnusedSterilisationRecordSuccessData,
  IUnusedSterilisationRecordJobData,
  IUnusedSterilisationRecordMigrationData
> {
  sourceEntity = new UnusedSterilisationRecordSourceEntity();
  destinationEntity = UNUSED_STERILISATION_RECORD_DESTINATION_ENTITY;

  override sourceEntities = {
    sterilisationRecords: new SterilisationRecordSourceEntity(),
  };

  override destinationEntities = {
    staff: new StafferDestinationEntity(),
    sterilisationMachines: new D4WSterilisationMachineDestinationEntity(),
    sterilisationCycles: new D4WSterilisationCycleDestinationEntity(),
    sterilisationEquipment: new D4WSterilisationEquipmentDestinationEntity(),
  };

  customMappings = {
    staff: new D4WStafferMappingHandler(),
    practices: new D4WPracticeMappingHandler(),
    sterilisationMachines: new D4WSterilisationMachineMappingHandler(),
    sterilisationEquipment: new D4WSterilisationEquipmentMappingHandler(),
  };

  get sourceCountComparison(): ISourceEntityHandler<D4WUnusedSterilisationRecord> {
    return this.sourceEntity;
  }

  sourceCountDataAccessor(
    data: IUnusedSterilisationRecordJobData
  ): PrimaryKey<ISourceEntityRecord> {
    return data.sourceSterilisationRecord.record.id;
  }

  getDestinationEntityRecordUid(
    data: IUnusedSterilisationRecordJobData
  ): string {
    return data.sourceSterilisationRecord.record.uid;
  }

  getMigratedData$(
    record: IDestinationEntityRecord<IUnusedSterilisationRecordSuccessData>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return Firestore.doc$(record.data.sterilisationRef).pipe(
      map((sterilisationRecord) => [
        { label: `Sterilisation Record`, data: sterilisationRecord },
      ])
    );
  }

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IUnusedSterilisationRecordJobData[]> {
    const practices$ = this.customMappings.practices.getRecords(translationMap);
    const brand$ = PracticeMigration.brand$(migration);
    const sterilisationMachines$ =
      this.customMappings.sterilisationMachines.getRecords(translationMap);
    const sterilisationEquipment$ =
      this.customMappings.sterilisationEquipment.getRecords(translationMap);
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));

    return snapshotCombineLatest([
      brand$,
      practices$,
      sterilisationMachines$,
      sterilisationEquipment$,
      staff$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntity,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          sourceSterilisationRecords,
          [
            brand,
            practices,
            sterilisationMachines,
            sterilisationEquipment,
            staff,
          ],
        ]) =>
          sourceSterilisationRecords.map((sourceSterilisationRecord) => ({
            sourceSterilisationRecord,
            brand,
            practices,
            sterilisationMachines,
            sterilisationEquipment,
            staff,
          }))
      )
    );
  }

  async buildMigrationData(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IUnusedSterilisationRecordJobData
  ): Promise<
    | IUnusedSterilisationRecordMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourceRecordId = this.sourceEntity
        .getSourceRecordId(data.sourceSterilisationRecord.data)
        .toString();

      const sterilisationRecord = await this._buildSterilisationRecord(
        sourceRecordId,
        data,
        translationMap
      );

      return {
        sterilisationRecord,
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourceSterilisationRecord,
        getError(error)
      );
    }
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IUnusedSterilisationRecordJobData,
    migrationData: IUnusedSterilisationRecordMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      const existingRecordRef = await translationMap.getDestination(
        migrationData.sterilisationRecord.sourceIdentifier,
        UNUSED_STERILISATION_RECORD_RESOURCE_TYPE
      );

      const sterilisationRef = await FirestoreMigrate.upsertDoc(
        Practice.sterilisationRecordCol({
          ref: migrationData.sterilisationRecord.practice.ref,
        }),
        migrationData.sterilisationRecord,
        existingRecordRef?.id
      );

      if (!existingRecordRef) {
        await translationMap.upsert({
          sourceIdentifier: migrationData.sterilisationRecord.sourceIdentifier,
          destinationIdentifier: sterilisationRef,
          resourceType: UNUSED_STERILISATION_RECORD_RESOURCE_TYPE,
        });
      }

      return this._buildSuccessResponse(
        data.sourceSterilisationRecord,
        sterilisationRef
      );
    } catch (error) {
      return this._buildErrorResponse(
        data.sourceSterilisationRecord,
        getError(error)
      );
    }
  }

  async hasMergeConflict(
    translationMap: TranslationMapHandler,
    data: IUnusedSterilisationRecordMigrationData
  ): Promise<IUnusedSterilisationRecordMigrationData | undefined> {
    const sterilisationRecordRef = await translationMap.getDestination(
      data.sterilisationRecord.sourceIdentifier,
      UNUSED_STERILISATION_RECORD_RESOURCE_TYPE
    );

    if (!sterilisationRecordRef) {
      return;
    }
    try {
      const existingSterilisationRecord = await Firestore.getDoc(
        asDocRef<ISterilisationRecord>(sterilisationRecordRef)
      );
      const hasMergeConflict = hasMergeConflicts(
        data.sterilisationRecord,
        existingSterilisationRecord
      );

      if (hasMergeConflict) {
        return {
          ...data,
          sterilisationRecord: {
            ...existingSterilisationRecord,
            sourceIdentifier: data.sterilisationRecord.sourceIdentifier,
          },
        };
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        'Error checking unused sterilisation record merge conflict:',
        error
      );
    }
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    data: IUnusedSterilisationRecordJobData,
    _migrationData: IUnusedSterilisationRecordMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: data.sourceSterilisationRecord.record.uid,
      label: data.sourceSterilisationRecord.record.label,
      sourceRef: data.sourceSterilisationRecord.record.id,
      status: DestinationEntityRecordStatus.MergeConflict,
    };
  }

  buildSkippedResponse(
    data: IUnusedSterilisationRecordJobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourceSterilisationRecord.record.uid,
      label: data.sourceSterilisationRecord.record.label,
      sourceRef: data.sourceSterilisationRecord.record.id,
      status: DestinationEntityRecordStatus.Skipped,
    };
  }

  protected _buildErrorResponse(
    sterilisationRecord: IGetRecordResponse<D4WUnusedSterilisationRecord>,
    error?: string
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: sterilisationRecord.record.uid,
      label: sterilisationRecord.record.label,
      sourceRef: sterilisationRecord.record.id,
      status: DestinationEntityRecordStatus.Failed,
      errorMessage: error || 'Failed to Migrate Unused Sterilisation Record',
      failData: {},
    };
  }

  protected _buildSuccessResponse(
    record: IGetRecordResponse<D4WUnusedSterilisationRecord>,
    sterilisationRef: DocumentReference<ISterilisationRecord>
  ): IDestinationEntityRecord<IUnusedSterilisationRecordSuccessData> {
    return {
      uid: record.record.uid,
      label: record.record.label,
      data: {
        sourceRef: record.record.id,
        sterilisationRef,
      },
      sourceRef: record.record.id,
      status: DestinationEntityRecordStatus.Migrated,
      migratedAt: toTimestamp(),
    };
  }

  private async _buildSterilisationRecord(
    sourceId: string,
    data: IUnusedSterilisationRecordJobData,
    translationMap: TranslationMapHandler
  ): Promise<
    ISterilisationRecord & IHasSourceIdentifier & { createdAt: Timestamp }
  > {
    const record = data.sourceSterilisationRecord.data;
    const practiceId = record.practice_id;
    const practiceMap = data.practices.find(
      (searchPractice) =>
        searchPractice.sourceIdentifier === practiceId.toString()
    );

    if (!practiceMap?.destinationIdentifier) {
      throw new Error(`Can't find practice with id ${practiceId}`);
    }
    const practice = await Firestore.getDoc(practiceMap?.destinationIdentifier);

    const staffer = await resolveD4WStaffer(
      record.staff_id.toString(),
      data.staff,
      translationMap,
      false
    );

    const cycleMapping = await translationMap.getBySource(
      record.cycle_id.toString(),
      STERILISATION_CYCLE_RESOURCE_TYPE
    );
    if (!cycleMapping?.destinationIdentifier) {
      throw new Error(`Can't find cycle with id ${record.cycle_id}`);
    }

    const cycleRef = await Firestore.getDoc(
      cycleMapping.destinationIdentifier as DocumentReference<ISterilisationCycle>
    );

    const equipment = record.pack_content_id
      ? await resolveMappedItem<SterilisableItem>(
          record.pack_content_id.toString(),
          data.sterilisationEquipment,
          translationMap,
          STERILISATION_EQUIPMENT_RESOURCE_TYPE
        )
      : undefined;

    return {
      ...SterilisationRecord.init({
        data: record.barcode,
        practice: toNamedDocument(practice),
        scannedBy: stafferToNamedDoc(staffer),
        cycle: toNamedDocument(cycleRef),
        pack: equipment ? toNamedDocument(equipment) : undefined,
      }),
      sourceIdentifier: `sterilistionRecord-${sourceId}`,
      createdAt:
        data.sourceSterilisationRecord.record.translations.dateSterilised,
      status: SterilisationStatus.Passed,
      deleted: false,
    };
  }
}
